// src/pages/ClientTasksPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path as needed
import { useAuth } from '../context/AuthContext'; // For potential future use or consistency
import { formatDate } from '../utils/styleUtils'; // Assuming you have this utility
import styles from '../styles/ClientTasksPage.module.css'; // Create this CSS module

const ClientTasksPage = () => {
    const { clientId } = useParams(); // Get clientId from URL
    const { currentUser, userRole } = useAuth(); // For potential future permission checks

    const [clientDetails, setClientDetails] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loadingClient, setLoadingClient] = useState(true);
    const [loadingTasks, setLoadingTasks] = useState(true);
    const [error, setError] = useState('');

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch Client Details
    useEffect(() => {
        if (!clientId) {
            setError("Client ID is missing from URL.");
            setLoadingClient(false);
            return;
        }
        setLoadingClient(true);
        const clientDocRef = doc(db, 'clients', clientId);

        const fetchClientData = async () => {
            try {
                const docSnap = await getDoc(clientDocRef);
                if (docSnap.exists()) {
                    if (isMounted.current) {
                        setClientDetails({ id: docSnap.id, ...docSnap.data() });
                    }
                } else {
                    if (isMounted.current) setError(`Client with ID ${clientId} not found.`);
                }
            } catch (err) {
                console.error("Error fetching client details:", err);
                if (isMounted.current) setError("Failed to load client details.");
            } finally {
                if (isMounted.current) setLoadingClient(false);
            }
        };

        fetchClientData();
    }, [clientId]);

    // Fetch Tasks for the Client
    useEffect(() => {
        if (!clientId) {
            setLoadingTasks(false);
            return; 
        }

        setLoadingTasks(true);
        // Clear previous task-related errors when clientId changes or on initial load for tasks
        if(error.includes("tasks")) setError('');


        const tasksCollectionRef = collection(db, 'tasks');
        const q = query(
            tasksCollectionRef,
            where('clientIds', 'array-contains', clientId), 
            orderBy('createdAt', 'desc')    
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            if (!isMounted.current) return;
            const fetchedTasks = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dueDate: doc.data().dueDate?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
            }));
            setTasks(fetchedTasks);
            setLoadingTasks(false);
        }, (err) => {
            if (!isMounted.current) return;
            console.error(`Error fetching tasks for client ${clientId}:`, err);
            if (err.code === 'failed-precondition' && err.message.includes('index')) {
                 setError(`Failed to load tasks: A database index on 'tasks' collection (clientIds ARRAY, createdAt DESC) might be required. Please contact your administrator.`);
            } else {
                setError("Failed to load tasks for this client.");
            }
            setLoadingTasks(false);
        });

        return () => unsubscribe(); 
    }, [clientId]); 

    if (loadingClient) {
        return <div className={styles.loadingIndicator}>Loading client details...</div>;
    }

    if (error && !clientDetails && !error.includes("tasks")) { 
        return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>;
    }
    
    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                {clientDetails ? (
                    <h1 className={styles.title}>
                        Tasks for Client: {clientDetails.name || 'Unknown Client'}
                        {clientDetails.company && <span className={styles.clientCompany}> ({clientDetails.company})</span>}
                    </h1>
                ) : (
                    <h1 className={styles.title}>Client Tasks</h1>
                )}
                <Link to="/clients" className={styles.backButton}>Back to Client List</Link>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}
            
            {!clientDetails && !loadingClient && !error && (
                 <p className={styles.noDataMessage}>Client data could not be loaded.</p>
            )}

            {clientDetails && ( 
                <>
                    {loadingTasks ? (
                        <div className={styles.loadingIndicator}>Loading tasks...</div>
                    ) : tasks.length === 0 ? (
                        <p className={styles.noDataMessage}>No tasks found for this client.</p>
                    ) : (
                        <div className={styles.tasksListContainer}>
                            <h2 className={styles.subHeader}>Associated Tasks</h2>
                            <ul className={styles.taskList}>
                                {tasks.map(task => (
                                    <li key={task.id} className={styles.taskItem}>
                                        <div className={styles.taskItemHeader}>
                                            <Link to={`/task/${task.id}`} className={styles.taskTitleLink}>
                                                {/* MODIFIED: Use task.taskName instead of task.title */}
                                                <h3>{task.taskName || 'Untitled Task'}</h3>
                                            </Link>
                                            <span className={`${styles.statusBadge} ${styles[`status${task.status?.replace(/\s+/g, '')}`]}`}>
                                                {task.status || 'N/A'}
                                            </span>
                                        </div>
                                        <p className={styles.taskMeta}>
                                            <strong>Priority:</strong> {task.priority || 'N/A'}
                                        </p>
                                        {task.assignedToName && (
                                            <p className={styles.taskMeta}>
                                                <strong>Assigned To:</strong> {task.assignedToName}
                                            </p>
                                        )}
                                        {task.dueDate && (
                                            <p className={styles.taskMeta}>
                                                <strong>Due Date:</strong> {formatDate(task.dueDate)}
                                            </p>
                                        )}
                                        <p className={styles.taskMeta}>
                                            <strong>Created:</strong> {task.createdAt ? formatDate(task.createdAt) : 'N/A'}
                                        </p>
                                        {task.description && (
                                            <p className={styles.taskDescriptionPreview}>
                                                {task.description.substring(0, 120)}{task.description.length > 120 ? '...' : ''}
                                            </p>
                                        )}
                                        <Link to={`/task/${task.id}`} className={styles.viewDetailsButton}>
                                            View Task Details
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ClientTasksPage;
